// 🔴 P1 (démo Laurent) — « Modifier un devis » : le PREMIER écran dit clairement QUI est le client, OÙ est le CHANTIER, et comment le joindre.
//
// ⚠️ Adresse du CLIENT (siège / facturation) ≠ adresse du CHANTIER (le SITE du dossier). Deux libellés distincts ; ⛔ aucun repli silencieux de l'une sur l'autre.
// Le chantier est lu depuis le SITE du dossier (`dossier.site_id` → GET /api/metier/sites/{id}, route existante). Dossier sans site ⇒ on le DIT, et on propose
// « Ajouter l'adresse du chantier » avec la MÊME fonction partagée que la visite Mario (`ajouterAdresseAuDossier` : anti-doublon normalisé, rattachement).
import { useCallback, useEffect, useState } from "react";
import { lireSite } from "../artisan/api";
import { ajouterAdresseAuDossier } from "../../renovation/entreeVisite";
import { EditText } from "../dossier/champsEdition";

const libelleAdresse = (s) => [s?.nom_site, s?.adresse, [s?.code_postal, s?.ville].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
const nomClient = (c) => c?.raison_sociale || [c?.prenom, c?.nom].filter(Boolean).join(" ") || c?.display_name || "";

export default function AdresseChantierDevis({ client, dossier, onAjoutee }) {
  const siteId = dossier?.site_id || null;
  const [site, setSite] = useState(null);
  const [erreurLecture, setErreurLecture] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ adresse: "", code_postal: "", ville: "" });
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    let vivant = true; setSite(null); setErreurLecture("");
    if (siteId) lireSite(siteId).then((s) => vivant && setSite(s)).catch((e) => vivant && setErreurLecture(e.message));
    return () => { vivant = false; };
  }, [siteId]);

  const ajouter = useCallback(async () => {
    setOccupe(true); setErreur("");
    try {
      await ajouterAdresseAuDossier(dossier.id, f, { clientId: dossier.client_id || client?.id });
      setOuvert(false); setF({ adresse: "", code_postal: "", ville: "" });
      onAjoutee?.();
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  }, [dossier, client, f, onAjoutee]);

  const contact = [client?.contact_secondaire_nom, client?.contact_secondaire_relation].filter(Boolean).join(" — ");
  const ligne = { color: "var(--oh-text)" };
  const etiquette = { color: "var(--oh-text-secondary)" };
  return (
    <div data-testid="devis-recap-chantier" style={{ display: "grid", gap: 8 }}>
      <div className="text-sm" data-testid="recap-client" style={ligne}>
        <span style={etiquette}>Client : </span>{nomClient(client) || "—"}
      </div>
      <div className="text-sm" data-testid="recap-coordonnees" style={ligne}>
        <span style={etiquette}>Joindre le client : </span>
        {[client?.telephone && `Tél. ${client.telephone}`, client?.email].filter(Boolean).join(" · ") || "non renseignées"}
        {contact && <span style={etiquette}> · Contact : {contact}</span>}
      </div>
      <div className="text-sm" data-testid="recap-chantier" style={ligne}>
        <span style={etiquette}>Adresse du chantier : </span>
        {siteId ? (site ? libelleAdresse(site) : (erreurLecture ? "illisible" : "chargement…")) : (
          <span data-testid="chantier-absent" style={{ color: "var(--oh-stat-amber-text)" }}>
            aucune adresse de chantier pour ce dossier (l'adresse du client n'est pas utilisée à sa place)
          </span>
        )}
      </div>
      {erreurLecture && <div role="alert" className="text-sm" style={{ color: "var(--oh-error-text)" }}>{erreurLecture}</div>}
      {!siteId && !ouvert && (
        <div>
          <button type="button" data-testid="chantier-ajouter" onClick={() => setOuvert(true)}
                  style={{ height: 36, padding: "0 16px", borderRadius: 8, background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" }}>
            Ajouter l'adresse du chantier
          </button>
        </div>
      )}
      {!siteId && ouvert && (
        <div data-testid="chantier-formulaire" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <EditText label="Adresse du chantier" value={f.adresse} onChange={(v) => setF((x) => ({ ...x, adresse: v }))} testId="chantier-adresse" />
          <EditText label="Code postal" value={f.code_postal} onChange={(v) => setF((x) => ({ ...x, code_postal: v }))} testId="chantier-cp" />
          <EditText label="Ville" value={f.ville} onChange={(v) => setF((x) => ({ ...x, ville: v }))} testId="chantier-ville" />
          <div>
            <button type="button" data-testid="chantier-enregistrer" disabled={occupe || !f.adresse.trim() || !f.ville.trim()} onClick={ajouter}
                    style={{ height: 36, padding: "0 16px", borderRadius: 8, background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" }}>
              {occupe ? "Enregistrement…" : "Enregistrer l'adresse du chantier"}
            </button>
          </div>
        </div>
      )}
      {erreur && <div role="alert" data-testid="chantier-erreur" className="text-sm" style={{ color: "var(--oh-error-text)" }}>{erreur}</div>}
    </div>
  );
}
